import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { EnvironmentService } from '@pedix-workspace/angular-utils';

/**
 * Tipo de storage disponible
 */
export type StorageType = 'local' | 'session' | 'cookies';

export type SameSite = 'Lax' | 'None' | 'Strict';

export interface CookieOptions {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: SameSite;
  partitioned?: boolean;
}

/**
 * Opciones para operaciones de storage
 */
export interface StorageOptions {
  /**
   * Tipo de storage a usar (obligatorio)
   */
  storageType: StorageType;

  /**
   * Time to live en milisegundos
   * Si se especifica, el valor expirará después de este tiempo
   */
  ttl?: number;

  /**
   * Prefijo para la key (ej: 'pedixapp', 'myapp')
   * Se concatena como: `${prefix}-${namespace}-${key}` o `${prefix}-${key}`
   */
  prefix?: string;

  /**
   * Namespace para agrupar keys (ej: 'cart', 'user', 'order')
   * Se concatena como: `${prefix}-${namespace}-${key}`
   */
  namespace?: string;

  /**
   * Si es true, elimina la key del storage después de leerla (patrón "read-once")
   */
  removeAfterRead?: boolean;
}

/**
 * Estructura interna para almacenar valores con TTL
 */
interface StorageEntry<T> {
  v: T; // value (nombre corto para minimizar bytes)
  t: number; // timestamp
  ttl?: number; // time to live en ms
}

/**
 * Información del entorno para debugging
 */
export interface StorageEnvironmentInfo {
  localStorageAvailable: boolean;
  sessionStorageAvailable: boolean;
  cookiesStorageAvailable: boolean;
  isInAppBrowser: boolean;
  userAgent: string;
  browserName?: string;
}

/**
 * Implementación de Storage en memoria que simula la API de localStorage/sessionStorage
 */
class MemoryStorage implements Storage {
  private data = new Map<string, string>();

  get length(): number {
    return this.data.size;
  }

  clear(): void {
    this.data.clear();
  }

  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  key(index: number): string | null {
    const keys = Array.from(this.data.keys());
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }
}

/**
 * Implementación de Storage usando cookies
 * Implementa la interfaz Storage usando CookieService internamente
 */
class CookieStorage implements Storage {
  constructor(
    private cookieService: SsrCookieService,
    private environment: EnvironmentService,
  ) {
    this.cookieService = cookieService;
  }

  get length(): number {
    return Object.keys(this.cookieService.getAll()).length;
  }

  clear(): void {
    this.cookieService.deleteAll();
  }

  getItem(key: string): string | null {
    const value = this.cookieService.get(key);
    return value || null;
  }

  key(index: number): string | null {
    const allCookies = this.cookieService.getAll();
    const keys = Object.keys(allCookies);
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    const cookieOptions = this.getDefaultCookieOptions();

    this.cookieService.delete(key, cookieOptions.path);
  }

  setItem(key: string, value: string): void {
    const cookieOptions = this.getDefaultCookieOptions();

    // Intentar extraer TTL del valor si está envuelto en StorageEntry
    try {
      const entry: StorageEntry<unknown> = JSON.parse(value);
      if (entry.ttl) {
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + entry.ttl);
        cookieOptions.expires = expirationDate;
      }
    } catch {
      // Si no es un StorageEntry, guardar sin expiración específica
    }
    this.cookieService.set(
      key,
      value,
      cookieOptions,
    );
  }

  /**
   * Obtiene las opciones de cookie predeterminadas basadas en el entorno
   */
  private getDefaultCookieOptions(): CookieOptions {
    let domain: string;

    const hostname = this.environment.hostname;

    if (hostname.includes('pedix.local')) {
      domain = '.pedix.local';
    } else if (hostname.includes('pedix.dev')) {
      domain = '.pedix.dev';
    } else if (hostname.includes('pedix.app')) {
      domain = '.pedix.app';
    } else {
      domain = hostname;
    }

    return {
      domain,
      sameSite: 'Lax',
      path: '/',
      secure: domain !== '.pedix.local',
    };
  }
}

/**
 * Servicio para acceso seguro a localStorage, sessionStorage y cookies con fallback a memoria.
 * Protege contra fallos en in-app browsers (Instagram, Facebook, etc.) que bloquean storage en iOS.
 */
@Injectable({
  providedIn: 'root',
})
export class WebStorageService {
  private readonly logger = inject(NGXLogger);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cookieService = inject(SsrCookieService);
  private readonly environment = inject(EnvironmentService);

  /**
   * Fallback storages en memoria
   */
  private readonly memoryStorages: Record<StorageType, Storage> = {
    local: new MemoryStorage(),
    session: new MemoryStorage(),
    cookies: new MemoryStorage(),
  };

  /**
   * Instancia de WebStorageCookie para cookies
   */
  private readonly cookieStorage: CookieStorage = new CookieStorage(
    this.cookieService,
    this.environment,
  );

  /**
   * Cache de disponibilidad (para no testear cada vez)
   */
  private availabilityCache: Record<StorageType, boolean | null> = {
    local: null,
    session: null,
    cookies: null,
  };

  /**
   * Guarda un valor en storage
   * @param key - Clave para identificar el valor (puede ser transformada por prefix/namespace)
   * @param value - Valor a guardar (será serializado)
   * @param options - Opciones que incluyen storageType (obligatorio), ttl, prefix y namespace (opcionales)
   * @returns true si se guardó en storage real, false si usó memoria fallback
   */
  setItem<T>(key: string, value: T, options: StorageOptions): boolean {
    const finalKey = this.buildKey(key, options);
    const wrappedValue = this.wrap(value, options.ttl);
    return this.setItemInternal(finalKey, wrappedValue, options.storageType);
  }

  /**
   * Obtiene un valor del storage
   * @param key - Clave del valor a obtener (puede ser transformada por prefix/namespace)
   * @param options - Opciones que incluyen storageType (obligatorio), prefix y namespace (opcionales)
   * @returns El valor deserializado o null si no existe o expiró
   */
  getItem<T>(key: string, options: StorageOptions): T | null {
    const finalKey = this.buildKey(key, options);
    const raw = this.getItemInternal(finalKey, options.storageType);
    const value = this.unwrap<T>(raw);

    if (options.removeAfterRead) {
      this.removeItem(key, options);
    }

    return value;
  }

  /**
   * Verifica si existe una key en el storage
   * @param key - Clave a verificar (puede ser transformada por prefix/namespace)
   * @param options - Opciones que incluyen storageType (obligatorio), prefix y namespace (opcionales)
   * @returns true si existe, false si no
   */
  hasItem(key: string, options: Omit<StorageOptions, 'removeAfterRead'>): boolean {
    return this.getItem(key, options) !== null;
  }

  /**
   * Elimina una key del storage
   * @param key - Clave a eliminar (puede ser transformada por prefix/namespace)
   * @param options - Opciones que incluyen storageType (obligatorio), prefix y namespace (opcionales)
   */
  removeItem(key: string, options: StorageOptions): void {
    const finalKey = this.buildKey(key, options);
    const storage = this.getStorage(options.storageType);

    try {
      storage.removeItem(finalKey);
    } catch (e) {
      this.logger.warn(`${options.storageType}Storage.removeItem failed`, {
        key: finalKey,
        error: e instanceof Error ? e.message : String(e),
        userAgent: this.getUserAgent(),
      });
    }
  }

  /**
   * Limpia todo el storage de un tipo
   * @param options - Opciones que incluyen storageType (obligatorio)
   */
  clear(options: StorageOptions): void {
    const storage = this.getStorage(options.storageType);

    try {
      storage.clear();
    } catch (e) {
      this.logger.warn(`${options.storageType}Storage.clear failed`, {
        error: e instanceof Error ? e.message : String(e),
        userAgent: this.getUserAgent(),
      });
    }
  }

  /**
   * Verifica si un tipo de storage está disponible
   * @param storageType - Tipo de storage a verificar (obligatorio)
   * @returns true si está disponible, false si no
   */
  isStorageAvailable(storageType: StorageType): boolean {
    // Usar cache si ya chequeamos
    if (this.availabilityCache[storageType] !== null) {
      return this.availabilityCache[storageType]!;
    }

    // SSR: no disponible
    if (isPlatformServer(this.platformId) && storageType !== 'cookies') {
      this.availabilityCache[storageType] = false;
      return false;
    }

    try {
      const storage = this.getBrowserStorage(storageType);
      const testKey = `__pedix_storage_test_${storageType}__`;

      storage.setItem(testKey, 'test');

      const retrieved = storage.getItem(testKey);

      storage.removeItem(testKey);

      const available = retrieved === 'test';
      this.availabilityCache[storageType] = available;

      if (!available) {
        this.logger.warn(`${storageType}Storage test failed`, {
          userAgent: this.getUserAgent(),
          isInAppBrowser: this.detectInAppBrowser(),
        });
      }

      return available;
    } catch (e) {
      this.logger.warn(`${storageType}Storage not available`, {
        error: e instanceof Error ? e.message : String(e),
        userAgent: this.getUserAgent(),
        isInAppBrowser: this.detectInAppBrowser(),
      });
      this.availabilityCache[storageType] = false;
      return false;
    }
  }

  /**
   * Obtiene información del entorno para debugging
   * @returns Información sobre disponibilidad de storage y tipo de browser
   */
  getEnvironmentInfo(): StorageEnvironmentInfo {
    if (isPlatformServer(this.platformId)) {
      return {
        localStorageAvailable: false,
        sessionStorageAvailable: false,
        cookiesStorageAvailable: false,
        isInAppBrowser: false,
        userAgent: 'SSR',
      };
    }

    const userAgent = navigator.userAgent;
    const isInAppBrowser = this.detectInAppBrowser();

    return {
      localStorageAvailable: this.isStorageAvailable('local'),
      sessionStorageAvailable: this.isStorageAvailable('session'),
      cookiesStorageAvailable: this.isStorageAvailable('cookies'),
      isInAppBrowser,
      userAgent,
      browserName: this.getBrowserName(userAgent),
    };
  }

  /**
   * Construye la key final aplicando prefix y namespace si existen
   * @param key - Key base proporcionada por el usuario
   * @param options - Opciones que pueden incluir prefix y namespace
   * @returns Key transformada o la original si no hay prefix/namespace
   *
   * Ejemplos:
   * - buildKey('user', {}) => 'user'
   * - buildKey('user', { prefix: 'app' }) => 'app-user'
   * - buildKey('user', { prefix: 'app', namespace: 'auth' }) => 'app-auth-user'
   * - buildKey('user', { namespace: 'auth' }) => 'user' (namespace sin prefix se ignora)
   */
  private buildKey(key: string, options: StorageOptions): string {
    const keyParts = [];

    if (options.prefix) {
      keyParts.push(options.prefix);
    }

    if (options.namespace) {
      keyParts.push(options.namespace);
    }

    keyParts.push(key);

    return keyParts.join('-');
  }

  /**
   * Envuelve un valor con metadata de TTL
   */
  private wrap<T>(value: T, ttl?: number): string {
    const entry: StorageEntry<T> = {
      v: value,
      t: Date.now(),
      ...(ttl && { ttl }),
    };
    return JSON.stringify(entry);
  }

  /**
   * Desenvuelve un valor y verifica TTL
   */
  private unwrap<T>(raw: string | null): T | null {
    if (!raw) return null;

    try {
      const entry = JSON.parse(raw);

      // NOTE: for backward compatibility before we started using StorageEntry
      if (!isStorageEntry<T>(entry)) {
        return <T | null>entry;
      }

      // Verificar TTL si existe
      if (entry.ttl && Date.now() - entry.t > entry.ttl) {
        return null; // Expirado
      }

      return entry.v;
    } catch {
      // Si falla el parse, podría ser data legacy sin wrapper
      return null;
    }
  }

  /**
   * Guarda un valor (ya serializado) en storage
   */
  private setItemInternal(key: string, value: string, storageType: StorageType): boolean {
    const storage = this.getStorage(storageType);

    // Si es SSR o storage no disponible, ya estamos usando memoria
    if (isPlatformServer(this.platformId) || !this.isStorageAvailable(storageType)) {
      storage.setItem(key, value);
      return false; // Usando fallback
    }

    // Intentar guardar en storage real
    try {
      storage.setItem(key, value);
      return true; // Éxito
    } catch (e) {
      this.logger.warn(`${storageType}Storage.setItem failed, using memory fallback`, {
        key,
        error: e instanceof Error ? e.message : String(e),
        userAgent: this.getUserAgent(),
        isInAppBrowser: this.detectInAppBrowser(),
      });

      // Guardar en memoria como fallback
      this.memoryStorages[storageType].setItem(key, value);
      return false;
    }
  }

  /**
   * Obtiene un valor (serializado) del storage
   */
  private getItemInternal(key: string, storageType: StorageType): string | null {
    const storage = this.getStorage(storageType);

    try {
      const value = storage.getItem(key);

      return value;
    } catch (e) {
      this.logger.warn(`${storageType}Storage.getItem failed, trying memory fallback`, {
        key,
        error: e instanceof Error ? e.message : String(e),
        userAgent: this.getUserAgent(),
      });

      // Intentar memoria como fallback
      return this.memoryStorages[storageType].getItem(key);
    }
  }

  /**
   * Obtiene la instancia del storage adecuada
   * En SSR o si storage no disponible, retorna memoria
   */
  private getStorage(storageType: StorageType): Storage {
    if (this.isStorageAvailable(storageType)) {
      return this.getBrowserStorage(storageType);
    }

    return this.memoryStorages[storageType];
  }

  /**
   * Obtiene la instancia del storage del browser según el tipo
   */
  private getBrowserStorage(storageType: StorageType): Storage {
    if (storageType === 'cookies') {
      return this.cookieStorage;
    }
    return storageType === 'local' ? localStorage : sessionStorage;
  }

  /**
   * Obtiene el user agent de forma segura
   */
  private getUserAgent(): string {
    if (isPlatformServer(this.platformId)) {
      return 'SSR';
    }
    return navigator.userAgent || 'unknown';
  }

  /**
   * Detecta si estamos en un in-app browser
   */
  private detectInAppBrowser(): boolean {
    if (isPlatformServer(this.platformId)) {
      return false;
    }

    const ua = navigator.userAgent || '';
    return /Instagram|FBAN|FBAV|FB_IAB|Twitter|LinkedInApp|TikTok|Snapchat|WhatsApp/i.test(ua);
  }

  /**
   * Extrae el nombre del browser del user agent
   */
  private getBrowserName(userAgent: string): string | undefined {
    if (/Instagram/i.test(userAgent)) return 'Instagram';
    if (/FBAN|FBAV|FB_IAB/i.test(userAgent)) return 'Facebook';
    if (/Twitter/i.test(userAgent)) return 'Twitter';
    if (/LinkedInApp/i.test(userAgent)) return 'LinkedIn';
    if (/TikTok/i.test(userAgent)) return 'TikTok';
    if (/Snapchat/i.test(userAgent)) return 'Snapchat';
    if (/WhatsApp/i.test(userAgent)) return 'WhatsApp';
    return undefined;
  }
}

function isStorageEntry<T>(value: unknown): value is StorageEntry<T> {
  return typeof value === 'object' && value !== null && 'v' in value;
}
